import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PrepTourAnchorId, PrepTourAnchorRegistry, PrepTourService, anchorId } from 'src/@prep/shared/tour';
import { GlobalUser } from '../../layout/model/global-user';
import { PlatformGlobalService } from '../../layout/service/platform-global.service';
import { WORKFLOW_ANCHORS } from '../guided-workflow.anchors';
import { isResumable, readWorkflowState, workflowStateKey, writeWorkflowState } from '../guided-workflow.state';
import {
  WORKFLOW_JOURNEY_GUIDE_ID, WORKFLOW_REMEDIATION_GUIDE_ID, WorkflowDashboardFile, WorkflowDashboardProbe, WorkflowFileId,
  WorkflowFileStatus, WorkflowState, WorkflowUploadOutcome, WorkflowUploadProbe,
} from '../guided-workflow.types';
import { WorkflowJourneyHooks, buildWorkflowJourneyGuide } from '../guides/workflow-journey.guide';
import { GuidedWorkflowI18nService } from './guided-workflow-i18n.service';

const ANCHOR_POLL_MS = 50;
const ANCHOR_WAIT_MS = 4000;
/** The dashboard list reloads after an upload: give the new card longer. */
const CARD_WAIT_MS = 15000;
/** After an upload: the dashboard settles before the card is pointed at. */
const CARD_SETTLE_MS = 2000;
/**
 * An upload that ended without the app navigating away within this time is
 * taken as failed (the upload screen stays on its page after an error).
 */
const UPLOAD_FAILED_GRACE_MS = 1500;
/** A navigation this long after an upload ended still counts as that upload's outcome. */
const UPLOAD_NAVIGATION_WINDOW_MS = 5000;
/** How often the upload flag is sampled while the upload screen is attached (outside the zone). */
const UPLOAD_WATCH_MS = 150;
/** Confirm button of the onboarding dialog (`prep-confirm-msg-config`). */
const CONFIRM_BUTTON_SELECTOR = 'prep-confirm-msg-config button[color="primary"]';

const UPLOAD_STEPS = new Set(['drop', 'uploadBtn', 'sample', 'confirm', 'uploading']);

/** The signed-in user as the app caches it (read-only fallback for routes without the toolbar, e.g. remediation). */
function readStoredUser(): GlobalUser | null {
  try {
    const raw = localStorage.getItem('account');
    const user = raw ? (JSON.parse(raw) as GlobalUser) : null;
    return user && user.id ? user : null;
  } catch {
    return null;
  }
}

/** Lower-case, no extension, letters and digits only — for comparing a queued file name with a listed one. */
function normalizeFileName(name: string | undefined | null): string {
  if (!name) return '';
  return name.replace(/\.[a-z0-9]{1,5}$/i, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

const sameFile = (a: WorkflowFileId | null | undefined, b: WorkflowFileId | null | undefined): boolean =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

/**
 * "Start work" — the task workflow from the dashboard to the exported PDF.
 *
 * Owns the workflow across screens: which stage it reached, which file it
 * follows and whether it is live in this session. It runs the layout leg
 * itself (dashboard → upload → file card) and hands over to the workbench
 * leg, which the remediation screen registers and starts through
 * {@link claimRemediation} once the document is on screen.
 *
 * The screens only report facts — the upload screen its queue
 * ({@link attachUploadPage}) and the onboarding confirm ({@link confirmOpened}),
 * the dashboard its file list ({@link attachDashboard}) and which card is the
 * tracked file ({@link cardAnchorFor}). The upload result is inferred from
 * where the upload screen navigates (dashboard / remediation / orders) or
 * from an upload that ended in place (failed); every decision is here or in
 * the guide definitions.
 *
 * Leaving ends the workflow quietly. The stage is saved per user + backend,
 * so the next "Start work" offers to continue with the uploaded file.
 */
@Injectable({ providedIn: 'root' })
export class GuidedWorkflowService implements OnDestroy {
  private readonly subs = new Subscription();
  private url: string;
  private user: GlobalUser | null = null;
  private storageKey = workflowStateKey(null, environment.backend);

  private _state: WorkflowState | null = null;
  /** The workflow runs in THIS session (not merely saved): the workbench leg may start by itself. */
  private _live = false;
  private _uploadPage: WorkflowUploadProbe | null = null;
  private _dashboard: WorkflowDashboardProbe | null = null;
  private _confirmOpen = false;
  private _sampleAccepted = false;
  private _outcome: WorkflowUploadOutcome | null = null;
  private wasUploading = false;
  private uploadEndedAt = 0;
  private uploadWatch: ReturnType<typeof setInterval> | null = null;
  /** First queued file name, kept past the upload screen's destruction (navigation lands before the outcome is read). */
  private lastFileName = '';

  private readonly _enabled = new BehaviorSubject<boolean>(false);
  /** Whether the launcher shows (see {@link enabled}). */
  readonly enabled$: Observable<boolean> = this._enabled.pipe(distinctUntilChanged());

  constructor(
    private readonly tour: PrepTourService,
    private readonly registry: PrepTourAnchorRegistry,
    private readonly router: Router,
    private readonly i18n: GuidedWorkflowI18nService,
    private readonly platform: PlatformGlobalService,
    private readonly zone: NgZone,
  ) {
    this.url = this.router.url;

    // The saved workflow follows the signed-in user.
    this.subs.add(this.platform.user.subscribe((user) => {
      this.user = user && user.id ? user : readStoredUser();
      const key = workflowStateKey(this.user ? this.user.id : null, environment.backend);
      if (key !== this.storageKey) {
        this.storageKey = key;
        this._state = readWorkflowState(key);
        this._live = false;
      } else if (!this._state) {
        this._state = readWorkflowState(key);
      }
      this._enabled.next(this.computeEnabled());
    }));

    this.subs.add(this.tour.events$.subscribe((e) => {
      if (e.guideId !== WORKFLOW_JOURNEY_GUIDE_ID && e.guideId !== WORKFLOW_REMEDIATION_GUIDE_ID) return;
      // Exit / Esc / leaving: no longer live — the stage stays saved for
      // resume. (`restart` is the workbench leg replacing the layout leg.)
      if (e.type === 'abort' && e.reason !== 'restart') this._live = false;
      if (e.type === 'complete' && e.guideId === WORKFLOW_REMEDIATION_GUIDE_ID) this.finish();
    }));

    this.subs.add(this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ).subscribe((url) => this.onNavigated(url)));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopUploadWatch();
  }

  // ── Gate ─────────────────────────────────────────────────────────────

  /**
   * Whether the launcher shows: the guidance gate (`subscription_configs.
   * enable_guidance` when the backend sends it; always on for CE accounts)
   * and a user who may upload on a plan that has the PREP tool.
   */
  get enabled(): boolean { return this._enabled.value; }

  private computeEnabled(): boolean {
    const user = this.user;
    const cfg = user ? user.new_configs : undefined;
    if (!user || !cfg) return false;
    const isCe = (user.username || '').replace(/.*@/, '') === 'continualengine.com';
    const guidance = cfg.subscription_configs ? (cfg.subscription_configs as { enable_guidance?: boolean }).enable_guidance : undefined;
    if (!isCe && guidance !== true) return false;
    if (cfg.plan_type === 'rsp') return false;
    if (cfg.user_access_level_type === 'admin-view') return false;
    const canUpload = cfg.file_access_configs ? cfg.file_access_configs.can_upload : undefined;
    return canUpload === undefined || canUpload === null ? true : canUpload;
  }

  // ── State ────────────────────────────────────────────────────────────

  /** Saved stage of the workflow, or null. */
  get state(): WorkflowState | null { return this._state; }

  /** One of the workflow's guides is on screen. */
  get running(): boolean {
    const g = this.tour.active;
    return !!g && (g.id === WORKFLOW_JOURNEY_GUIDE_ID || g.id === WORKFLOW_REMEDIATION_GUIDE_ID);
  }

  /** Launcher pressed: begin, or offer to continue with the file already uploaded. */
  start(launcher?: HTMLElement | null): void {
    if (!this.enabled || this.tour.active) return;
    const resume = isResumable(this._state);
    this._outcome = null;
    this._confirmOpen = false;
    this._sampleAccepted = false;
    this.uploadEndedAt = 0;
    this.lastFileName = '';
    this._live = true;
    this.tour.start(buildWorkflowJourneyGuide(this.i18n.t, this.hooks, { resume }), {
      returnFocusTo: launcher || null,
      skipWelcome: resume,
    });
  }

  // ── Upload screen ────────────────────────────────────────────────────

  /** The upload screen is on: expose its live facts. Returns the detach function. */
  attachUploadPage(probe: WorkflowUploadProbe): () => void {
    this._uploadPage = probe;
    this.wasUploading = false;
    this.uploadEndedAt = 0;
    // Sample the upload flag on our own clock: a step's predicate may
    // short-circuit before reading it (the onboarding branch does), and the
    // navigation that follows an upload must still be recognised as its outcome.
    this.stopUploadWatch();
    this.zone.runOutsideAngular(() => {
      this.uploadWatch = setInterval(() => { if (this._uploadPage === probe) this.readUploading(); }, UPLOAD_WATCH_MS);
    });
    this.tour.notify();
    return () => {
      if (this._uploadPage === probe) {
        this.readUploading();
        this._uploadPage = null;
        this.stopUploadWatch();
      }
    };
  }

  private stopUploadWatch(): void {
    if (this.uploadWatch !== null) { clearInterval(this.uploadWatch); this.uploadWatch = null; }
  }

  /**
   * The onboarding confirmation dialog opened. Anchors its confirm button
   * for the guide; call the returned function with the answer when it closes.
   */
  confirmOpened(): (accepted: boolean) => void {
    if (!this.running) return () => {};
    this._confirmOpen = true;
    this.tour.notify();
    const release = this.anchorWhenRendered(CONFIRM_BUTTON_SELECTOR, WORKFLOW_ANCHORS.upload.confirm);
    return (accepted) => {
      release();
      this._sampleAccepted = accepted;
      this._confirmOpen = false;
      this.tour.notify();
    };
  }

  /** The confirmed onboarding file could not be queued. */
  sampleFailed(): void {
    this._sampleAccepted = false;
    this.tour.notify();
  }

  /** The upload batch settled and the screen decided where the user goes next (explicit form; navigation is inferred otherwise). */
  reportUploadOutcome(outcome: WorkflowUploadOutcome): void {
    if (!this.journeyActive()) return;
    const fileName = (this._uploadPage ? this._uploadPage.firstFileName() : '') || this.lastFileName;
    this._sampleAccepted = false;
    if (outcome.kind === 'dashboard') {
      const known = this._state ? this._state.fileId : null;
      this.patchState({ stage: 'card', fileId: outcome.fileIds.length ? outcome.fileIds[0] : known, fileName });
    } else if (outcome.kind === 'remediation') {
      this.patchState({ stage: 'remediation', fileId: outcome.fileId, fileName });
    }
    this._outcome = outcome;
    this.tour.notify();
  }

  // ── Dashboard ────────────────────────────────────────────────────────

  /** The dashboard is on: expose its file list. Returns the detach function. */
  attachDashboard(probe: WorkflowDashboardProbe): () => void {
    this._dashboard = probe;
    this.tour.notify();
    return () => {
      if (this._dashboard === probe) this._dashboard = null;
    };
  }

  /** Anchor id for a file card: the workflow's card anchor on the tracked file, '' on every other. */
  cardAnchorFor(file: WorkflowDashboardFile): string {
    const s = this._state;
    if (!s || s.stage === 'upload') return '';
    return this.isTrackedFile(s, file) ? WORKFLOW_ANCHORS.dashboard.card : '';
  }

  // ── Remediation ──────────────────────────────────────────────────────

  /**
   * The remediation screen shows `fileId`: true when the live workflow is
   * waiting for exactly this (the caller then starts the workbench leg).
   */
  claimRemediation(fileId: WorkflowFileId | null): boolean {
    const s = this._state;
    if (!s || fileId === null || fileId === undefined || !this._live || !this.enabled) return false;
    if (s.stage === 'upload') return false;
    if (s.fileId !== null && !sameFile(s.fileId, fileId)) return false;
    this.patchState({ stage: 'remediation', fileId: s.fileId === null ? fileId : s.fileId });
    return true;
  }

  /** The workflow is over: nothing left to resume. */
  finish(): void {
    this._live = false;
    this.setState(null);
  }

  /** Resolves true once the anchor is registered (false on timeout or when the guide ended). */
  waitForAnchor(id: PrepTourAnchorId, timeoutMs = ANCHOR_WAIT_MS): Promise<boolean> {
    return this.waitFor(() => this.registry.has(id), timeoutMs);
  }

  // ── Internals ────────────────────────────────────────────────────────

  private readonly hooks: WorkflowJourneyHooks = {
    onUploadPage: () => this.onUploadPage(),
    inRemediation: () => this.url.startsWith('/pdf-remediation/'),
    samplesAvailable: () => (this._uploadPage ? this._uploadPage.samplesAvailable() : false),
    fileCount: () => (this._uploadPage ? this._uploadPage.fileCount() : 0),
    uploading: () => this.readUploading(),
    confirmOpen: () => this._confirmOpen,
    sampleAccepted: () => this._sampleAccepted,
    outcome: () => this.resolveOutcome(),
    path: () => (this._state ? this._state.path : null),
    fileStatus: () => this.fileStatus(),
    fileName: () => (this._state ? this._state.fileName : ''),
    choosePath: (path) => this.patchState({ path }),
    clearOutcome: () => { this._outcome = null; this.uploadEndedAt = 0; },
    startOver: () => this.setState(null),
    finish: () => this.finish(),
    goToStep: (id) => this.tour.goToStep(id),
    openDashboard: async () => {
      this.ensureState();
      await this.navigateTo('/dashboard');
      await this.waitForAnchor(WORKFLOW_ANCHORS.dashboard.add);
    },
    openUpload: async () => {
      this.ensureState();
      await this.navigateTo('/file-upload');
      await this.waitFor(() => this._uploadPage !== null, ANCHOR_WAIT_MS);
    },
    openDashboardCard: async () => {
      const fromUpload = this._outcome !== null;
      await this.navigateTo('/dashboard');
      const s = this._state;
      const known = !!s && (s.fileId !== null || !!s.fileName);
      await this.waitForAnchor(WORKFLOW_ANCHORS.dashboard.card, known ? CARD_WAIT_MS : ANCHOR_POLL_MS);
      // Arriving from the upload: let the dashboard settle before pointing.
      if (fromUpload) await this.waitFor(() => false, CARD_SETTLE_MS);
    },
    waitForAnchor: (id) => this.waitForAnchor(id),
  };

  private journeyActive(): boolean {
    const g = this.tour.active;
    return !!g && g.id === WORKFLOW_JOURNEY_GUIDE_ID;
  }

  private onUploadPage(): boolean {
    return this.url.startsWith('/file-upload');
  }

  /** Read the upload flag and remember when an upload ended (for the failed-in-place inference). */
  private readUploading(): boolean {
    const now = this._uploadPage ? this._uploadPage.uploading() : false;
    if (this._uploadPage) {
      const name = this._uploadPage.firstFileName();
      if (name) this.lastFileName = name;
    }
    if (now && !this.wasUploading) this.uploadEndedAt = 0;
    if (!now && this.wasUploading) this.uploadEndedAt = Date.now();
    this.wasUploading = now;
    return now;
  }

  /** The reported outcome, or "failed" when an upload ended and the screen stayed put. */
  private resolveOutcome(): WorkflowUploadOutcome | null {
    if (this._outcome) return this._outcome;
    if (this.uploadEndedAt && this.onUploadPage() && !this.readUploading()
      && Date.now() - this.uploadEndedAt > UPLOAD_FAILED_GRACE_MS) {
      this._outcome = { kind: 'failed' };
      this._sampleAccepted = false;
    }
    return this._outcome;
  }

  /** A navigation: infer the upload outcome, then end the layout leg if the user left its screens. */
  private onNavigated(url: string): void {
    const previous = this.url;
    this.url = url;
    if (!this.journeyActive() || this.tour.phase !== 'step') return;
    const step = this.tour.step;
    const stepId = step ? step.id : '';
    const uploadWasInFlight = this.wasUploading || (!!this.uploadEndedAt && Date.now() - this.uploadEndedAt < UPLOAD_NAVIGATION_WINDOW_MS);
    // Inference only fills in for upload paths that did not report explicitly (`_outcome` already set).
    if (!this._outcome && UPLOAD_STEPS.has(stepId) && uploadWasInFlight && previous.startsWith('/file-upload') && !url.startsWith('/file-upload')) {
      const inferred = this.outcomeFromUrl(url);
      if (inferred) { this.reportUploadOutcome(inferred); return; }
    }
    if (!this.allowsRoute(url)) this.tour.stop('left');
    else this.tour.notify();
  }

  private outcomeFromUrl(url: string): WorkflowUploadOutcome | null {
    const remediation = /^\/pdf-remediation\/([^/?#]+)/.exec(url);
    if (remediation) return { kind: 'remediation', fileId: remediation[1] };
    if (url.startsWith('/dashboard')) return { kind: 'dashboard', fileIds: [] };
    if (url.startsWith('/orders')) return { kind: 'orders' };
    return null;
  }

  /** Routes the layout leg may be on; anywhere else ends it. */
  private allowsRoute(url: string): boolean {
    if (url.startsWith('/dashboard') || url.startsWith('/file-upload')) return true;
    // Opening the file (or an upload that lands straight in the workbench)
    // ends the leg through its own stop, not as "left".
    if (url.startsWith('/pdf-remediation/')) {
      const step = this.tour.step;
      const id = step ? step.id : '';
      return id === 'card' || id === 'cardMissing' || (!!this._outcome && this._outcome.kind === 'remediation');
    }
    if (url.startsWith('/orders')) return !!this._outcome && this._outcome.kind === 'orders';
    return false;
  }

  private isTrackedFile(s: WorkflowState, file: WorkflowDashboardFile): boolean {
    if (s.fileId !== null) return sameFile(s.fileId, file.id);
    // No id known (older upload paths): match the name the list shows, tolerant of
    // case, extension and punctuation differences in how the backend stores it.
    const wanted = normalizeFileName(s.fileName);
    if (!wanted) return false;
    return normalizeFileName(file.original_filename) === wanted || normalizeFileName(file.filename) === wanted;
  }

  /** The tracked file as the dashboard list shows it right now (learns the id once the list reveals it). */
  private fileStatus(): WorkflowFileStatus {
    const s = this._state;
    if (!s || (s.fileId === null && !s.fileName)) return 'unknown';
    const files = this._dashboard ? this._dashboard.files() : [];
    const file = files.find((f) => this.isTrackedFile(s, f));
    if (!file) return 'missing';
    if (s.fileId === null && file.id !== null && file.id !== undefined) this.patchState({ fileId: file.id });
    if (file.status === 'failed' || file.process_state === 'failed' || file.process_state === 'PWD-INVALID') return 'failed';
    return file.processed ? 'ready' : 'processing';
  }

  private async navigateTo(path: string): Promise<void> {
    if (this.url.startsWith(path)) return;
    try { await this.router.navigate([path]); } catch { /* guard refused: the stop explains or is skipped */ }
  }

  private waitFor(done: () => boolean, timeoutMs: number): Promise<boolean> {
    const guide = this.tour.active;
    return new Promise((resolve) => {
      const deadline = Date.now() + timeoutMs;
      const check = (): void => {
        if (done()) { resolve(true); return; }
        if (this.tour.active !== guide || Date.now() >= deadline) { resolve(false); return; }
        this.zone.runOutsideAngular(() => setTimeout(() => this.zone.run(check), ANCHOR_POLL_MS));
      };
      check();
    });
  }

  /** Register `id` on the first element matching `selector` once it renders; returns the release function. */
  private anchorWhenRendered(selector: string, id: PrepTourAnchorId): () => void {
    let unregister: (() => void) | null = null;
    let released = false;
    void this.waitFor(() => document.querySelector(selector) !== null, ANCHOR_WAIT_MS).then((found) => {
      const el = found ? document.querySelector<HTMLElement>(selector) : null;
      if (!el || released) return;
      unregister = this.registry.register(anchorId(id), el, 4);
    });
    return () => {
      released = true;
      if (unregister) unregister();
    };
  }

  private ensureState(): void {
    if (!this._state) this.setState({ stage: 'upload', path: null, fileId: null, fileName: '', at: Date.now() });
  }

  private patchState(patch: Partial<Omit<WorkflowState, 'at'>>): void {
    const base: WorkflowState = this._state || { stage: 'upload', path: null, fileId: null, fileName: '', at: 0 };
    this.setState({ ...base, ...patch, at: Date.now() });
  }

  private setState(state: WorkflowState | null): void {
    this._state = state;
    writeWorkflowState(this.storageKey, state);
  }
}
